import { useQuery } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { api, jsonBody } from "../../api/client";
import { useAuth } from "../../auth/AuthProvider";
import { useSensitiveAction } from "../../auth/SensitiveActionGate";
import { formatDateTime } from "../../lib/format";
import {
  Card,
  CardHeader,
  DataTable,
  ErrorPanel,
  Field,
  FormActions,
  Notice,
  PageHeader,
  StatusPill,
} from "../../ui/components";
import { SkeletonCard, SkeletonPageHeader } from "../../ui/skeletons";

interface MFAStatus {
  enabled: boolean;
  recoveryCodesRemaining: number;
  recentlyVerified: boolean;
  verifiedUntil?: string;
}

interface Enrollment {
  secret: string;
  otpauthUrl: string;
  period: number;
  digits: number;
}

interface RecoveryCodes {
  recoveryCodes: string[];
}

interface UserSession {
  id: string;
  userAgent: string;
  createdAt: string;
  lastSeenAt: string;
  expiresAt: string;
  currentSession: boolean;
}

interface SessionList {
  sessions: UserSession[];
}

export function SecuritySettings() {
  const auth = useAuth();
  const sensitive = useSensitiveAction({
    lockedTitle: "Confirm it’s you",
    lockedDescription:
      "Enter a current authenticator code before changing your password or turning off two-step verification.",
  });
  const status = useQuery({
    queryKey: ["auth", "mfa"],
    queryFn: () => api<MFAStatus>("/api/v1/auth/mfa"),
  });
  const sessions = useQuery({
    queryKey: ["auth", "sessions"],
    queryFn: () => api<SessionList>("/api/v1/auth/sessions"),
  });
  const [enrollment, setEnrollment] = useState<Enrollment | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [disableConfirmed, setDisableConfirmed] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<unknown>();
  const [success, setSuccess] = useState("");

  function startAction() {
    setWorking(true);
    setError(undefined);
    setSuccess("");
  }

  function protectedError(caught: unknown) {
    if (!sensitive.markProtectedError(caught)) setError(caught);
  }

  async function beginEnrollment() {
    startAction();
    try {
      setEnrollment(
        await api<Enrollment>("/api/v1/auth/mfa/enrollment", {
          method: "POST",
          body: jsonBody({}),
        }),
      );
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function confirmEnrollment(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    startAction();
    try {
      const result = await api<RecoveryCodes>("/api/v1/auth/mfa/enrollment/confirm", {
        method: "POST",
        body: jsonBody({ code: String(form.get("code")) }),
      });
      setRecoveryCodes(result.recoveryCodes);
      setEnrollment(null);
      setSuccess(
        "Two-step verification is on. Save the recovery codes now; they will not be shown again.",
      );
      await Promise.all([status.refetch(), auth.refresh()]);
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function disableMFA() {
    if (!disableConfirmed) return;
    startAction();
    try {
      await api<void>("/api/v1/auth/mfa", { method: "DELETE" });
      setSuccess("Two-step verification is off.");
      setDisableConfirmed(false);
      await Promise.all([status.refetch(), auth.refresh()]);
    } catch (caught) {
      protectedError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function changePassword(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const newPassword = String(form.get("newPassword") ?? "");
    if (newPassword !== String(form.get("confirmPassword") ?? "")) {
      setError(new Error("The new password and confirmation do not match."));
      return;
    }
    startAction();
    try {
      await api<void>("/api/v1/auth/password/change", {
        method: "POST",
        body: jsonBody({
          currentPassword: String(form.get("currentPassword") ?? ""),
          newPassword,
        }),
      });
      formElement.reset();
      setSuccess("Password changed. Every other active session was signed out.");
      await sessions.refetch();
    } catch (caught) {
      protectedError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function revokeSession(session: UserSession) {
    startAction();
    try {
      await api<void>(`/api/v1/auth/sessions/${session.id}`, { method: "DELETE" });
      if (session.currentSession) {
        window.location.assign("/login");
        return;
      }
      setSuccess("Session signed out.");
      await sessions.refetch();
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function revokeOtherSessions() {
    startAction();
    try {
      const result = await api<{ revoked: number }>("/api/v1/auth/sessions", { method: "DELETE" });
      setSuccess(
        `${result.revoked} other session${result.revoked === 1 ? " was" : "s were"} signed out.`,
      );
      await sessions.refetch();
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  if (status.isLoading || sessions.isLoading)
    return (
      <>
        <SkeletonPageHeader />
        <div className="content-grid">
          <SkeletonCard className="span-6" />
          <SkeletonCard className="span-6" />
          <SkeletonCard className="span-6" />
          <SkeletonCard className="span-6" />
        </div>
      </>
    );
  if (status.error || sessions.error) return <ErrorPanel error={status.error ?? sessions.error} />;

  return (
    <>
      <PageHeader
        title="Account security"
        description="Protect your sign-in, change your password, and review devices using your account."
      />
      {error ? <ErrorPanel error={error} /> : null}
      {success ? <Notice tone="success">{success}</Notice> : null}
      <div className="content-grid">
        <Card className="span-6">
          <CardHeader
            title="Two-step verification"
            description="Use an authenticator app for an extra check whenever you sign in."
            actions={<StatusPill value={status.data?.enabled ? "enabled" : "disabled"} />}
          />
          {status.data?.enabled ? (
            <div className="stack">
              <Notice tone="success" title="Enabled">
                {status.data.recoveryCodesRemaining} unused recovery codes remain.
              </Notice>
              <label className="check-field">
                <input
                  type="checkbox"
                  checked={disableConfirmed}
                  onChange={(event) => setDisableConfirmed(event.target.checked)}
                />
                <span>I understand that turning this off makes my account less secure.</span>
              </label>
              <button
                className="button danger"
                type="button"
                disabled={working || !sensitive.unlocked || !disableConfirmed}
                onClick={disableMFA}
              >
                Turn off two-step verification
              </button>
            </div>
          ) : (
            <div className="stack">
              <Notice tone="warning">
                Set up two-step verification before changing billing, privacy, web addresses, team
                access, or other protected settings.
              </Notice>
              <button
                className="button primary"
                type="button"
                disabled={working}
                onClick={beginEnrollment}
              >
                {working ? "Working…" : "Set up two-step verification"}
              </button>
            </div>
          )}
          {enrollment ? (
            <form className="form-stack" onSubmit={confirmEnrollment}>
              <Notice tone="info" title="Add this account">
                <span>
                  Open <a href={enrollment.otpauthUrl}>the setup link</a> in your authenticator app,
                  or enter this key: <strong className="mono">{enrollment.secret}</strong>. The app
                  will create a new {enrollment.digits}-digit code every {enrollment.period}{" "}
                  seconds.
                </span>
              </Notice>
              <Field label="Authenticator code">
                <input
                  name="code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  required
                />
              </Field>
              <FormActions
                submitting={working}
                submitLabel="Confirm and enable"
                onCancel={() => setEnrollment(null)}
              />
            </form>
          ) : null}
          {recoveryCodes.length ? (
            <Card>
              <CardHeader
                title="Recovery codes: save now"
                description="Each code works once. Store them outside this application."
              />
              <div className="recovery-codes">
                {recoveryCodes.map((code) => (
                  <code key={code}>{code}</code>
                ))}
              </div>
              <button
                className="button secondary"
                type="button"
                onClick={() => navigator.clipboard.writeText(recoveryCodes.join("\n"))}
              >
                Copy codes
              </button>
            </Card>
          ) : null}
        </Card>

        <Card className="span-6">
          <CardHeader
            title="Confirm protected changes"
            description="For your security, confirm your identity just before changing protected settings."
          />
          {status.data?.enabled ? (
            sensitive.view
          ) : (
            <Notice tone="warning">Set up two-step verification first.</Notice>
          )}
          <Notice tone="info" title="For this sign-in only">
            This confirmation expires automatically and never gives you access beyond your role or
            assigned venues.
          </Notice>
        </Card>

        <Card className="span-6">
          <form className="form-stack" onSubmit={changePassword}>
            <CardHeader
              title="Change password"
              description="Changing your password keeps this session and signs out every other session."
            />
            <Field label="Current password">
              <input
                name="currentPassword"
                type="password"
                autoComplete="current-password"
                required
              />
            </Field>
            <Field
              label="New password"
              hint="At least 12 characters and different from the current password."
            >
              <input
                name="newPassword"
                type="password"
                autoComplete="new-password"
                minLength={12}
                required
              />
            </Field>
            <Field label="Confirm new password">
              <input
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                minLength={12}
                required
              />
            </Field>
            <FormActions
              submitting={working}
              disabled={Boolean(status.data?.enabled && !sensitive.unlocked)}
              submitLabel="Change password"
            />
          </form>
        </Card>

        <Card className="span-6">
          <CardHeader
            title="Active sessions"
            description="Review devices that can access your account and revoke anything you do not recognize."
            actions={
              <button
                className="button secondary small"
                type="button"
                disabled={working}
                onClick={revokeOtherSessions}
              >
                Sign out other sessions
              </button>
            }
          />
          <DataTable
            caption="Active account sessions"
            items={sessions.data?.sessions ?? []}
            getKey={(session) => session.id}
            columns={[
              {
                label: "Device",
                render: (session) => (
                  <>
                    <strong>{session.currentSession ? "This device" : "Other device"}</strong>
                    <br />
                    <small>{session.userAgent || "Unknown browser"}</small>
                  </>
                ),
              },
              { label: "Last active", render: (session) => formatDateTime(session.lastSeenAt) },
              { label: "Expires", render: (session) => formatDateTime(session.expiresAt) },
              {
                label: "Action",
                render: (session) => (
                  <button
                    className={
                      session.currentSession ? "button danger small" : "button secondary small"
                    }
                    type="button"
                    disabled={working}
                    onClick={() => revokeSession(session)}
                  >
                    {session.currentSession ? "Sign out here" : "Sign out"}
                  </button>
                ),
              },
            ]}
          />
        </Card>
      </div>
    </>
  );
}
